import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
  Alert,
  Snackbar,
  Stack,
} from "@mui/material";
import {
  DexServiceConfig,
  RpcConfig,
  DexConfig,
  WalletConfig,
  LogLevel,
  CreateLogParams
} from '../../../types/dex';
import { useDexService } from '../../../contexts/DexServiceContext';
import RpcConfigSection from './RpcConfigSection';
import DexConfigSection from './DexConfigSection';
import WalletConfigSection from './WalletConfigSection';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dex-config-tabpanel-${index}`}
      aria-labelledby={`dex-config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

/**
 * DEX配置表单组件
 * 用于配置DexService所需的各项参数
 */
const DexConfigForm: React.FC = () => {
  const { addLog, initialize, setDexServiceConfig } = useDexService();

  // 标签页状态
  const [tabValue, setTabValue] = useState(0);

  // 配置状态
  const [rpcConfig, setRpcConfig] = useState<RpcConfig>({
    chainId: 56, // BSC默认chainId
    url: "https://bsc-mainnet.nodereal.io/v1/4b06d775febe481fbc614202d940a7cf",
    name: "Binance Smart Chain",
    symbol: "BNB", // 添加BSC原生代币符号
  });

  const [dexConfig, setDexConfig] = useState<DexConfig>({
    router: {
      address: "0x10ED43C718714eb63d5aA57B78B54704E256024E", // PancakeSwap V2路由器地址
      name: "PancakeSwap V2",
    },
    factory: {
      address: "0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73", // PancakeSwap V2工厂地址
    },
  });

  const [walletConfigs, setWalletConfigs] = useState<WalletConfig[]>(localStorage.getItem("walletKeys") ? JSON.parse(localStorage.getItem("walletKeys") || "[]") : []);
  const [selectedWalletIndices, setSelectedWalletIndices] = useState<number[]>(
    []
  );

  // 通知状态
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // 处理标签页切换
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 处理RPC配置变更
  const handleRpcConfigChange = (newConfig: RpcConfig) => {
    setRpcConfig(newConfig);
  };

  // 处理DEX配置变更
  const handleDexConfigChange = (newConfig: DexConfig) => {
    setDexConfig(newConfig);
  };

  // 处理钱包配置变更
  const handleWalletConfigsChange = (newConfigs: WalletConfig[]) => {
    setWalletConfigs(newConfigs);
    saveConfigToStorage({
      rpc: rpcConfig,
      dex: dexConfig,
      wallets: newConfigs,
    });
  };

  // 保存配置到本地存储
  const saveConfigToStorage = useCallback(
    (config: DexServiceConfig) => {
      try {
        // 合并 wallets 的配置
        const oldConfig:DexServiceConfig = JSON.parse(
          localStorage.getItem("dexServiceConfig") || "{}"
        );
        const map = new Map<string, WalletConfig>();
        oldConfig.wallets.forEach(wallet => map.set(wallet.address, wallet));
        config.wallets.forEach(wallet => map.set(wallet.address, wallet));
        const mergedConfig: DexServiceConfig = {
          ...oldConfig,
          ...config,
          wallets: Array.from(map.values()),
        };
        // 更新配置状态
        setDexServiceConfig(mergedConfig);
        // 保存主配置
        localStorage.setItem("dexServiceConfig", JSON.stringify(mergedConfig));

        // 单独保存钱包私钥
        const walletKeys = mergedConfig.wallets.map((wallet) => ({
          address: wallet.address,
          privateKey: wallet.privateKey,
        }));
        localStorage.setItem("walletKeys", JSON.stringify(walletKeys));

        const log: CreateLogParams = {
          level: LogLevel.SUCCESS,
          message: "配置已保存到本地",
          data: {
            network: config.rpc.name,
            dex: config.dex.router.name,
            walletCount: config.wallets.length,
          },
        };
        addLog(log);
      } catch (error) {
        const log: CreateLogParams = {
          level: LogLevel.ERROR,
          message: "保存配置失败",
          data: {
            error: error instanceof Error ? error.message : "未知错误",
          },
        };
        addLog(log);
      }
    },
    [addLog]
  );

  // 监听配置变化并保存
  useEffect(() => {
    const config: DexServiceConfig = {
      rpc: rpcConfig,
      dex: dexConfig,
      wallets: walletConfigs,
    };
    saveConfigToStorage(config);
    
  }, [rpcConfig, dexConfig, walletConfigs, saveConfigToStorage]);
  
  // 处理钱包配置变更
  const handleWalletConfigChange = useCallback((newWallets: WalletConfig[]) => {
    setWalletConfigs(newWallets);
    const log: CreateLogParams = {
      level: LogLevel.INFO,
      message: '钱包配置已更新',
      data: {
        count: newWallets.length,
        addresses: newWallets.filter(w => w.address).map(w => w.address)
      }
    };
    addLog(log);
  }, [addLog]);


  // 处理MUI版WalletManager钱包导入
  const handleMUIWalletImport = useCallback(
    (newWallets: WalletConfig[]) => {
      // 合并现有钱包和新导入的钱包
      const updatedWallets = [...walletConfigs, ...newWallets];

      handleWalletConfigsChange(updatedWallets);

      // 显示通知
      setNotification({
        open: true,
        message: `成功导入${newWallets.length}个钱包`,
        severity: "success",
      });
    },
    [walletConfigs, handleWalletConfigsChange]
  );

  // 处理MUI版WalletManager钱包选择
  const handleMUIWalletSelect = useCallback((indices: number[]) => {
    setSelectedWalletIndices(indices);
  }, []);

  // 处理MUI版WalletManager钱包删除
  const handleMUIWalletDelete = useCallback(
    (indices: number[]) => {
      if (indices.length === 0) return;

      if (window.confirm(`确定要删除选中的 ${indices.length} 个钱包吗？`)) {
        // 按索引从大到小排序，以便从后向前删除，避免索引变化
        const sortedIndices = [...indices].sort((a, b) => b - a);

        // 创建新的钱包数组，移除要删除的钱包
        const updatedWallets = [...walletConfigs];
        sortedIndices.forEach((index) => {
          updatedWallets.splice(index, 1);
        });

        handleWalletConfigsChange(updatedWallets);
        setSelectedWalletIndices([]);

        setNotification({
          open: true,
          message: `已删除 ${indices.length} 个钱包`,
          severity: "success",
        });
      }
    },
    [walletConfigs, handleWalletConfigsChange]
  );


  // 处理保存配置
  const handleSaveConfig = useCallback(async () => {
    if (!rpcConfig || !dexConfig || !walletConfigs.length) {
      const log: CreateLogParams = {
        level: LogLevel.ERROR,
        message: "配置不完整，无法保存",
        data: {
          hasRpcConfig: !!rpcConfig,
          hasDexConfig: !!dexConfig,
          walletCount: walletConfigs.length,
        },
      };
      addLog(log);
      setNotification({
        open: true,
        message: "配置不完整，请检查所有必填项",
        severity: "error",
      });
      return;
    }

    const config: DexServiceConfig = {
      rpc: rpcConfig,
      dex: dexConfig,
      wallets: walletConfigs,
    };

    try {
      // 先初始化DexService
      await initialize(config);

      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: "配置已保存并更新",
        data: {
          network: rpcConfig.name,
          dex: dexConfig.router.name,
          walletCount: walletConfigs.length,
        },
      };
      addLog(log);

      setNotification({
        open: true,
        message: "配置已保存并更新",
        severity: "success",
      });

    } catch (error) {
      const log: CreateLogParams = {
        level: LogLevel.ERROR,
        message: "保存配置失败",
        data: {
          error: error instanceof Error ? error.message : "未知错误",
        },
      };
      addLog(log);

      setNotification({
        open: true,
        message: "保存配置失败",
        severity: "error",
      });
    }
  }, [rpcConfig, dexConfig, walletConfigs, addLog, initialize]);

  // 加载配置
  const handleLoadConfig = useCallback(() => {
    try {
      // 1. 加载主配置
      const savedConfig = localStorage.getItem("dexServiceConfig");
      addLog({
        level: LogLevel.INFO,
        message: "正在加载主配置...",
        data: { configExists: !!savedConfig },
      });

      // 2. 加载钱包私钥
      const savedWalletKeys = localStorage.getItem("walletKeys");
      addLog({
        level: LogLevel.INFO,
        message: "正在加载钱包私钥...",
        data: { keysExist: !!savedWalletKeys },
      });

      if (savedConfig) {
        const config: DexServiceConfig = JSON.parse(savedConfig);

        // 3. 合并钱包私钥
        if (savedWalletKeys) {
          const walletKeys: { address: string; privateKey: string }[] =
            JSON.parse(savedWalletKeys);
          addLog({
            level: LogLevel.INFO,
            message: "合并钱包私钥...",
            data: {
              walletCount: config.wallets.length,
              savedKeysCount: walletKeys.length,
            },
          });

          config.wallets = config.wallets.map((wallet) => {
            const savedKey = walletKeys.find(
              (key) => key.address === wallet.address
            );
            return savedKey
              ? { ...wallet, privateKey: savedKey.privateKey }
              : wallet;
          });
        }

        // 4. 更新状态
        setRpcConfig(config.rpc);
        setDexConfig(config.dex);
        setWalletConfigs(config.wallets);

        addLog({
          level: LogLevel.SUCCESS,
          message: "配置加载完成",
          data: {
            network: config.rpc.name,
            dex: config.dex.router.name,
            walletCount: config.wallets.length,
            hasKeys: config.wallets.every((w) => w.privateKey),
          },
        });
      }
    } catch (error) {
      addLog({
        level: LogLevel.ERROR,
        message: "加载配置失败",
        data: {
          error: error instanceof Error ? error.message : "未知错误",
        },
      });
    }
  }, [addLog, setRpcConfig, setDexConfig, setWalletConfigs]);

  // 关闭通知
  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // 获取网络原生代币符号
  const getNetworkSymbol = (name: string): string => {
    const symbolMap: { [key: string]: string } = {
      "Binance Smart Chain": "BNB",
      Ethereum: "ETH",
      Polygon: "MATIC",
      Avalanche: "AVAX",
    };

    for (const [networkName, symbol] of Object.entries(symbolMap)) {
      if (name.includes(networkName)) {
        return symbol;
      }
    }
    return name.split(" ")[0]; // 如果找不到匹配，使用名称的第一个词
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="DEX配置选项卡"
        >
          <Tab
            label="RPC配置"
            id="dex-config-tab-0"
            aria-controls="dex-config-tabpanel-0"
          />
          <Tab
            label="DEX配置"
            id="dex-config-tab-1"
            aria-controls="dex-config-tabpanel-1"
          />
          <Tab
            label="钱包配置"
            id="dex-config-tab-2"
            aria-controls="dex-config-tabpanel-2"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <RpcConfigSection config={rpcConfig} onChange={handleRpcConfigChange} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <DexConfigSection config={dexConfig} onChange={handleDexConfigChange} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
 
          
          {/* 保留原有的WalletConfigSection组件，用于管理私钥 */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              钱包配置详情
            </Typography>
            <WalletConfigSection wallets={walletConfigs} onChange={handleWalletConfigChange} />
          </Box>
        </Box>
      </TabPanel>

      <Box sx={{ mt: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={handleLoadConfig} color="secondary">
            加载配置
          </Button>
          <Button
            onClick={handleSaveConfig}
            variant="contained"
            color="primary"
          >
            保存配置
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DexConfigForm;
